import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api-client';
import '../styles/CopyDatabaseManual.css';

const CopyDatabaseManual = () => {
    const [serviceId, setServiceId] = useState('');
    const [date, setDate] = useState('');
    const [commitIds, setCommitIds] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const [environmentInfo, setEnvironmentInfo] = useState(null);
    const [environmentLoading, setEnvironmentLoading] = useState(true);

    useEffect(() => {
        const fetchEnvironmentInfo = async () => {
            try {
                const response = await apiFetch('/api/environment');
                if (!response.ok) {
                    throw new Error('Failed to fetch environment info');
                }
                const data = await response.json();
                setEnvironmentInfo(data);
            } catch (error) {
                console.error('Error fetching environment info:', error);
                // Default to hiding the feature if we can't determine environment
                setEnvironmentInfo({
                    isDatabaseExtractEnabled: false,
                    environment: 'unknown'
                });
            } finally {
                setEnvironmentLoading(false);
            }
        };

        fetchEnvironmentInfo();
    }, []);

    // Check if form is valid
    const isFormValid = () => {
        const hasServiceId = serviceId.trim() !== '';
        const hasCommitIds = commitIds.trim() !== '';
        const hasDate = date.trim() !== '';
        return hasServiceId && (hasCommitIds || hasDate);
    };

    // Helper function to determine message type based on content
    const getMessageType = (message) => {
        if (message.includes('already exists in the target database') ||
            message.includes('No data found in the source database')) {
            return 'warning';
        }
        return 'success';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid()) {
            setStatus('Error: Please fill in Service ID and either Commit IDs or Date');
            return;
        }

        setLoading(true);
        setStatus('');

        try {
            const payload = {
                ServiceId: parseInt(serviceId),
                Date: date.trim() !== '' ? date : new Date().toISOString().split('T')[0]
            };

            // Only include CommitIds if it has a value
            if (commitIds.trim() !== '') {
                payload.CommitIds = commitIds.trim();
            }

            const response = await apiFetch('/api/dev/mirror', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            const responseText = await response.text();
            if (response.ok) {
                const messageType = getMessageType(responseText);
                setStatus(`${messageType === 'warning' ? 'Warning' : 'Success'}: ${responseText}`);
            } else {
                setStatus(`Error: ${responseText || `Server error (HTTP ${response.status})`}`);
            }
        } catch (error) {
            setStatus(`Error: ${error?.message ?? 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    // Show loading while fetching environment info
    if (environmentLoading) {
        return (
            <div className="db-copy-container-loading">
                <h3>Database Copy Tool</h3>
                <div>Loading environment information...</div>
            </div>
        );
    }

    // Show restriction message if not enabled in this environment
    if (environmentInfo?.isDatabaseExtractEnabled === false) {
        return (
            <div className="db-copy-container-restricted">
                <h3>Database Copy Tool</h3>
                <div className="db-copy-restriction-message">
                    <p><strong>⚠️ Environment Restriction</strong></p>
                    <p>This feature is only available in <strong>development</strong> and <strong>test</strong> environments.</p>
                    <p>Current environment: <strong>{environmentInfo?.environment || 'Unknown'}</strong></p>
                </div>
            </div>
        );
    }

    const hasCommitIds = commitIds.trim() !== '';
    const hasDate = date.trim() !== '';

    // Show the full functionality if enabled
    return (
        <div className="db-copy-container">
            {/* Environment indicator */}
            <div className="db-copy-environment-indicator">
                <strong>Environment:</strong> {environmentInfo?.environment}
                <span className="db-copy-environment-indicator-check">✓ Database copy enabled</span>
            </div>

            <h3>Copy Database Rows</h3>

            <form onSubmit={handleSubmit}>
                <div className="db-copy-form-group">
                    <label className="db-copy-form-label">
                        Service ID:
                    </label>
                    <input
                        type="number"
                        value={serviceId}
                        onChange={e => setServiceId(e.target.value)}
                        required
                        placeholder="Enter Service ID (e.g., 0)"
                        className="db-copy-form-input"
                    />
                </div>
                <div className="db-copy-form-group">
                    <label className="db-copy-form-label">
                        Commit IDs (comma-separated):
                    </label>
                    <input
                        type="text"
                        value={commitIds}
                        onChange={e => setCommitIds(e.target.value)}
                        placeholder="e.g. 064725fa9f79f7bfbcb40453c3f5a1a90c55448c"
                        className="db-copy-form-input"
                        disabled={hasDate}
                    />
                </div>

                <div className="db-copy-form-group">
                    <label className="db-copy-form-label">
                        Date:
                    </label>
                    <input
                        type="date"
                        value={date}
                        onChange={e => setDate(e.target.value)}
                        required={!hasCommitIds}
                        className="db-copy-form-input"
                        disabled={hasCommitIds}
                    />
                </div>

                <button
                    type="submit"
                    disabled={loading || !isFormValid()}
                    className="db-copy-submit-button"
                >
                    {loading ? 'Executing Mirror' : 'Execute Mirror'}
                </button>
            </form>

            {loading && (
                <div className="db-copy-loading-message">
                    <div>⏳ Processing request...</div>
                    <div className="db-copy-loading-subtitle">
                        This may take a few moments
                    </div>
                </div>
            )}

            {status && (
                <div className={`db-copy-status-message ${status.startsWith('Success') ? 'db-copy-status-success' :
                    status.startsWith('Warning') ? 'db-copy-status-warning' :
                        'db-copy-status-error'
                    }`}>
                    {status.startsWith('Warning') && <span style={{ marginRight: '8px' }}>⚠️</span>}
                    {status}
                </div>
            )}
        </div>
    );
};

export default CopyDatabaseManual;